import type {
  ClientEntitlements,
  Permissions,
} from '@nursio-link/common/authorization';
import {isAuthorized, isEntitled} from '@nursio-link/common/authorization';
import {filter, pipe} from 'ramda';
import type {ReactNode} from 'react';
import {Link} from 'react-router-dom';

import logo from '@/assets/nursio.svg';
import signet from '@/assets/nursio-signet.svg';
import {Separator} from '@/components/ui/separator';
import {useGroups} from '@/hooks/useAuth';
import {useEntitlements} from '@/hooks/useEntitlements';
import {cn} from '@/utils/ui';

import MenuItem from './MenuItem';

export type MenuItemElement = {
  path: string;
  text: string;
  icon: ReactNode;
  permission?: Permissions;
  entitlement?: ClientEntitlements;
};

export type MenuProps = {
  items: MenuItemElement[];
  isSmall?: boolean;
};

const Menu = ({items, isSmall}: MenuProps) => {
  const currentGroups = useGroups();
  const entitlements = useEntitlements();

  const filterByAuthorization = currentGroups
    ? filter(({permission}: MenuItemElement) =>
        permission ? isAuthorized(currentGroups, permission) : true
      )
    : () => [];

  const filterByEntitlement = entitlements
    ? filter(({entitlement}: MenuItemElement) =>
        entitlement ? isEntitled(entitlements, entitlement) : true
      )
    : () => [];

  const permittedItems = pipe(
    filterByAuthorization,
    filterByEntitlement
  )(items) as MenuItemElement[];

  const {rootItems, regularItems} = permittedItems.reduce(
    (groups, item) => {
      if (item.permission === 'FULL_ACCESS' && item.entitlement === 'ROOT') {
        groups.rootItems.push(item);
      } else {
        groups.regularItems.push(item);
      }

      return groups;
    },
    {
      regularItems: [] as MenuItemElement[],
      rootItems: [] as MenuItemElement[],
    }
  );

  return (
    <div
      className={cn('flex flex-col items-center justify-center', {
        'gap-12 p-4': !isSmall,
        'gap-4 px-1 py-4': isSmall,
      })}
    >
      <Link to="/">
        {!isSmall && (
          <img className={cn('transition-all')} src={logo} alt="Nursiolink" />
        )}
        {isSmall && (
          <img
            className={cn('w-[30px] transition-all')}
            src={signet}
            alt="Nursiolink"
          />
        )}
      </Link>
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-1">
          {regularItems.map(item => (
            <MenuItem key={item.path} isSmall={isSmall} {...item} />
          ))}
        </div>
        {!!rootItems.length && (
          <>
            <Separator />
            {!isSmall && (
              <span className="px-4 text-xs font-semibold text-neutral-dark uppercase">
                Root access
              </span>
            )}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                {rootItems.map(item => (
                  <MenuItem key={item.path} isSmall={isSmall} {...item} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Menu;
